/**
 * PreDeparturePhase.js
 *
 * Pre-departure phase interface
 *
 * Features:
 * - Voyage summary card
 * - FT-201: Master designation dropdown (certificate-based qualification)
 * - Pre-departure checklist (required + optional)
 * - Let Go button (when all required checks complete)
 * - Cancel voyage option
 */

import React, { useState, useCallback, useMemo } from 'react';
import {
  Ship,
  MapPin,
  Users,
  UserCheck,
  Clock,
  Check,
  Circle,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  Play,
  X,
  FileText,
  Phone,
  Anchor
} from 'lucide-react';

// Config
import { VOYAGE_STATUS_LABELS } from '../../../../../config/operationsConfig';

// Components
import FuelInputModal from './FuelInputModal';
import FuelChecklistItem from './FuelChecklistItem';
import Dropdown from '../../../../common/Dropdown';
import TimePickerModal from './TimePickerModal'; // FT-269: event-time decoupling

// FT-201: Master qualification
import { useCrew } from '../../../../../hooks/useCrew';
import { useVessel } from '../../../../../contexts/VesselContext';
import {
  getQualifiedMasters,
  createMasterDesignationSnapshot
} from '../../../../../utils/crew/masterQualificationCheck';
import { portLabel } from '../../../../../utils/voyage/portLabel';

/**
 * PreDeparturePhase Component
 */
const PreDeparturePhase = ({ context, voyage }) => {
  const { voyage: voyageHook, logbook, showAlert, showConfirm, currentUser } = context;
  const { updatePreDepartureCheck, letGo, cancelVoyage, canLetGo, setDepartureFuel, setMasterDesignation } = voyageHook;

  // FT-201: Master qualification hooks
  const { crew: allCrew } = useCrew();
  const { vesselSettings, currentVessel } = useVessel(); // FT-269: currentVessel.timezone feeds TimePickerModal

  // Local state
  const [showOptional, setShowOptional] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showFuelModal, setShowFuelModal] = useState(false);
  const [letGoConfirmed, setLetGoConfirmed] = useState(false);
  const [selectedMasterId, setSelectedMasterId] = useState(voyage?.masterId || null);
  // FT-269: event-time picker. Opens before any letGo() call; the resulting
  // { eventTime, eventTimezone, delayReason } is threaded through.
  const [showTimePicker, setShowTimePicker] = useState(false);
  const [pendingTimeOptions, setPendingTimeOptions] = useState(null);

  // Separate required and optional checks
  const requiredChecks = useMemo(() => {
    return voyage.preDepartureChecks?.filter(c => c.required) || [];
  }, [voyage.preDepartureChecks]);

  const optionalChecks = useMemo(() => {
    return voyage.preDepartureChecks?.filter(c => !c.required) || [];
  }, [voyage.preDepartureChecks]);

  // Count completed
  const requiredCompleted = requiredChecks.filter(c => c.completed).length;
  const optionalCompleted = optionalChecks.filter(c => c.completed).length;

  // Can proceed?
  const canProceed = canLetGo(voyage.id);

  // FT-201: Get crew assigned to this voyage and compute qualified masters
  const crewOnVoyage = useMemo(() => {
    if (!voyage?.crew || !allCrew) return [];
    // voyage.crew is array of crew IDs
    return allCrew.filter(c => voyage.crew.includes(c.id) && c.status === 'active');
  }, [voyage?.crew, allCrew]);

  const qualifiedMasters = useMemo(() => {
    if (!crewOnVoyage.length || !vesselSettings) return [];
    return getQualifiedMasters(crewOnVoyage, vesselSettings);
  }, [crewOnVoyage, vesselSettings]);

  // FT-201: Transform qualified masters into Dropdown options format
  const masterOptions = useMemo(() => {
    return qualifiedMasters.map(({ crew, qualificationResult }) => {
      const cocCert = qualificationResult.qualifyingCerts?.find(c => c.type === 'coc');
      const certBasis = cocCert?.cert?.type || cocCert?.cert?.title || 'Qualified';
      return {
        value: crew.id,
        label: `${crew.name || crew.full_name} (${certBasis})`
      };
    });
  }, [qualifiedMasters]);

  // Handle check toggle
  const handleToggleCheck = useCallback(async (checkId, currentValue) => {
    await updatePreDepartureCheck(voyage.id, checkId, !currentValue);
  }, [voyage.id, updatePreDepartureCheck]);

  // FT-269: Handle Let Go — first ask for event time via TimePickerModal.
  // The actual letGo() call happens in handleTimePickerConfirm once the
  // Master selects "use current time" or enters an actual time + reason.
  const handleLetGo = useCallback(async () => {
    if (!canProceed) {
      await showAlert(
        'Cannot Depart',
        'Please complete all required pre-departure checks before departing.',
        'warning'
      );
      return;
    }
    setShowTimePicker(true);
  }, [canProceed, showAlert]);

  // FT-269: invoked by TimePickerModal once the Master confirms.
  const handleTimePickerConfirm = useCallback(async (timeOptions) => {
    setShowTimePicker(false);
    // Fuel-flow branching is unchanged from the pre-FT-269 confirm flow.
    const fuelCheck = voyage?.preDepartureChecks?.find(c => c.type === 'fuel');
    const fuelAlreadyCaptured = fuelCheck?.completed && fuelCheck?.fuelData;

    if (fuelAlreadyCaptured) {
      setIsTransitioning(true);
      const result = await letGo(voyage.id, timeOptions);

      if (result?.error === 'VOYAGE_ALREADY_ACTIVE') {
        showAlert('Cannot depart — another voyage is already underway. Complete or cancel that voyage first.');
        setIsTransitioning(false);
        return;
      }

      if (result === true) {
        await logbook.logVoyageEvent({
          eventType: 'let_go',
          voyageId: voyage.id,
          voyageNumber: voyage.voyageNumber,
          from: voyage.from?.name,
          to: voyage.to?.name,
          crewCount: voyage.crewCount,
          passengerCount: voyage.passengerCount,
          departureFuel: voyage.departureFuel || fuelCheck.fuelData?.percentage,
          timestamp: timeOptions?.eventTime || new Date().toISOString()
        });
      }
      setIsTransitioning(false);
    } else {
      // No fuel captured yet — stash the event-time options and show the
      // fuel modal. The fuel-confirm handler reads pendingTimeOptions.
      setPendingTimeOptions(timeOptions);
      setLetGoConfirmed(true);
      setShowFuelModal(true);
    }
  }, [voyage, letGo, logbook, showAlert]);

  // Handle fuel capture and complete LET GO.
  // FT-269: thread pendingTimeOptions through so eventTime/eventTimezone/delayReason
  // captured by the TimePickerModal land on the persisted voyage event.
  const handleFuelConfirm = useCallback(async (fuelLevel) => {
    setShowFuelModal(false);
    setIsTransitioning(true);

    // Save departure fuel
    if (fuelLevel !== null) {
      await setDepartureFuel(voyage.id, fuelLevel);
    }

    // Complete LET GO with the time options captured earlier (may be undefined
    // for the legacy direct-fuel path, in which case letGo defaults to now).
    const result = await letGo(voyage.id, pendingTimeOptions || undefined);

    // Handle error case: another voyage already active
    if (result?.error === 'VOYAGE_ALREADY_ACTIVE') {
      showAlert('Cannot depart — another voyage is already underway. Complete or cancel that voyage first.');
      setIsTransitioning(false);
      setLetGoConfirmed(false);
      setPendingTimeOptions(null);
      return;
    }

    if (result === true) {
      // Log to main logbook
      await logbook.logVoyageEvent({
        eventType: 'let_go',
        voyageId: voyage.id,
        voyageNumber: voyage.voyageNumber,
        from: voyage.from?.name,
        to: voyage.to?.name,
        crewCount: voyage.crewCount,
        passengerCount: voyage.passengerCount,
        departureFuel: fuelLevel,
        timestamp: pendingTimeOptions?.eventTime || new Date().toISOString()
      });
    }

    setIsTransitioning(false);
    setLetGoConfirmed(false);
    setPendingTimeOptions(null);
  }, [voyage, letGo, setDepartureFuel, logbook, showAlert, pendingTimeOptions]);

  // Handle fuel modal cancel
  const handleFuelCancel = useCallback(() => {
    setShowFuelModal(false);
    setLetGoConfirmed(false);
  }, []);

  // FT-201: Handle Master selection with audit snapshot
  const handleMasterChange = useCallback(async (masterId) => {
    setSelectedMasterId(masterId);

    if (!masterId) {
      // Clearing master selection
      await setMasterDesignation(voyage.id, null, null);
      return;
    }

    // Find the selected crew member and their qualification result
    const selectedCrew = crewOnVoyage.find(c => c.id === masterId);
    const qualResult = qualifiedMasters.find(q => q.crew.id === masterId);

    if (!selectedCrew || !qualResult) {
      console.warn('[PreDeparturePhase] Master selection failed - crew or qualification not found');
      return;
    }

    // Create immutable audit snapshot at moment of selection
    const snapshot = createMasterDesignationSnapshot(selectedCrew, vesselSettings, currentUser?.id);

    // Save to voyage record
    await setMasterDesignation(voyage.id, masterId, snapshot);

    console.log('[PreDeparturePhase] Master designated:', selectedCrew.name || selectedCrew.full_name);
  }, [voyage.id, crewOnVoyage, qualifiedMasters, vesselSettings, setMasterDesignation, currentUser]);

  // Handle Cancel Voyage
  const handleCancelVoyage = useCallback(async () => {
    const reason = await showConfirm(
      'Cancel Voyage?',
      'Are you sure you want to cancel this voyage? This cannot be undone.',
      'Cancel Voyage',
      'Keep Voyage',
      'warning'
    );

    if (reason) {
      await cancelVoyage(voyage.id, 'Cancelled by user');
    }
  }, [voyage.id, cancelVoyage, showConfirm]);

  // Handle fuel check value change (inline fuel item)
  const handleFuelCheckChange = useCallback(async (checkId, fuelData) => {
    console.log('[PreDeparturePhase] Fuel check change:', { voyageId: voyage?.id, checkId, fuelData });

    if (!voyage?.id) {
      console.error('[PreDeparturePhase] No voyage ID for fuel check update');
      return;
    }

    // Update the check with fuel data and mark as completed
    const success = await updatePreDepartureCheck(voyage.id, checkId, true, { fuelData });
    console.log('[PreDeparturePhase] updatePreDepartureCheck result:', success);

    // Also save as departure fuel (only if update succeeded or as backup)
    if (fuelData && setDepartureFuel) {
      await setDepartureFuel(voyage.id, fuelData.percentage || fuelData.value);
    }
  }, [voyage, updatePreDepartureCheck, setDepartureFuel]);

  // Render check item
  const renderCheckItem = (check) => {
    // Handle fuel type check items
    if (check.type === 'fuel') {
      return (
        <FuelChecklistItem
          key={check.id}
          label={check.text}
          value={check.fuelData || null}
          onChange={(fuelData) => handleFuelCheckChange(check.id, fuelData)}
          required={check.required}
          disabled={false}
        />
      );
    }

    // Standard checkbox item
    return (
      <button
        key={check.id}
        onClick={() => handleToggleCheck(check.id, check.completed)}
        className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-colors text-left ${
          check.completed
            ? 'bg-green-50 border-green-200'
            : check.required
              ? 'bg-white border-gray-200 hover:border-cyan-300'
              : 'bg-gray-50 border-gray-200 hover:border-cyan-300'
        }`}
        style={{ minHeight: '56px' }}
      >
        <div className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center ${
          check.completed
            ? 'bg-green-500 border-green-500'
            : check.required
              ? 'border-blue-400'
              : 'border-gray-300'
        }`}>
          {check.completed && <Check className="h-4 w-4 text-white" />}
        </div>

        <div className="flex-1 min-w-0">
          <p className={`font-medium ${check.completed ? 'text-green-700 line-through' : 'text-gray-900'}`}>
            {check.text}
          </p>
          {check.completed && check.completedBy && (
            <p className="text-xs text-green-600 mt-0.5">
              {check.completedBy} at {new Date(check.completedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </p>
          )}
        </div>

        {check.required && !check.completed && (
          <span className="px-2 py-0.5 text-xs bg-amber-100 text-amber-700 rounded-full">
            Required
          </span>
        )}
      </button>
    );
  };

  return (
    <div className="p-4 space-y-4">
      {/* Voyage Summary Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-cyan-600 px-4 py-3 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Ship className="h-5 w-5" />
              <span className="font-medium">Voyage {voyage.voyageNumber}</span>
            </div>
            <span className="px-2 py-0.5 text-xs bg-white/20 rounded-full">
              {VOYAGE_STATUS_LABELS.pre_departure}
            </span>
          </div>
        </div>

        <div className="p-4 space-y-3">
          {/* Route */}
          <div className="flex items-center gap-3">
            <MapPin className="h-5 w-5 text-gray-400" />
            <div>
              <p className="text-sm text-gray-500">Route</p>
              <p className="font-medium text-gray-900">
                {portLabel(voyage.from)} → {portLabel(voyage.to)}
              </p>
            </div>
          </div>

          {/* Crew & Passengers */}
          <div className="flex gap-6">
            <div className="flex items-center gap-3">
              <Users className="h-5 w-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Crew</p>
                <p className="font-medium text-gray-900">{voyage.crewCount}</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <UserCheck className="h-5 w-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Passengers</p>
                <p className="font-medium text-gray-900">{voyage.passengerCount}</p>
              </div>
            </div>
          </div>

          {/* FT-201: Master Designation */}
          <div className="flex items-start gap-3">
            <Anchor className="h-5 w-5 text-gray-400 mt-1" />
            <div className="flex-1">
              <p className="text-sm text-gray-500 mb-1">Designated Master</p>
              {qualifiedMasters.length > 0 ? (
                <Dropdown
                  value={selectedMasterId || ''}
                  onChange={(value) => handleMasterChange(value || null)}
                  options={masterOptions}
                  placeholder="Select Master..."
                  width="w-full"
                />
              ) : (
                <div className="flex items-center gap-2 text-amber-600 text-sm">
                  <AlertTriangle className="h-4 w-4" />
                  <span>No qualified masters on voyage crew</span>
                </div>
              )}
              {selectedMasterId && voyage.masterDesignation && (
                <p className="text-xs text-green-600 mt-1">
                  ✓ Master designated with certificate snapshot
                </p>
              )}
            </div>
          </div>

          {/* Shore Contact */}
          {voyage.shoreNotified && (
            <div className="flex items-center gap-2 text-sm text-green-600">
              <Phone className="h-4 w-4" />
              Shore contact notified
            </div>
          )}
        </div>
      </div>

      {/* Progress Summary */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-medium text-gray-900">Pre-Departure Checks</h3>
          <span className={`text-sm font-medium ${
            canProceed ? 'text-green-600' : 'text-amber-600'
          }`}>
            {requiredCompleted}/{requiredChecks.length} required
          </span>
        </div>

        {/* Progress Bar */}
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all duration-300 ${
              canProceed ? 'bg-green-500' : 'bg-cyan-500'
            }`}
            style={{ width: `${(requiredCompleted / requiredChecks.length) * 100}%` }}
          />
        </div>
      </div>

      {/* Required Checks */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-500" />
          <h3 className="font-medium text-gray-900">Required Checks</h3>
        </div>

        <div className="p-3 space-y-2">
          {requiredChecks.map(check => renderCheckItem(check))}
        </div>
      </div>

      {/* Optional Checks */}
      {optionalChecks.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <button
            onClick={() => setShowOptional(!showOptional)}
            className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50"
          >
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-gray-400" />
              <h3 className="font-medium text-gray-900">
                Optional Checks ({optionalCompleted}/{optionalChecks.length})
              </h3>
            </div>
            {showOptional ? (
              <ChevronUp className="h-5 w-5 text-gray-400" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-400" />
            )}
          </button>

          {showOptional && (
            <div className="p-3 pt-0 space-y-2">
              {optionalChecks.map(check => renderCheckItem(check))}
            </div>
          )}
        </div>
      )}

      {/* Action Buttons */}
      <div className="space-y-3">
        {/* Let Go Button */}
        <button
          onClick={handleLetGo}
          disabled={!canProceed || isTransitioning}
          className={`w-full flex items-center justify-center gap-2 py-4 px-6 rounded-lg font-medium transition-colors ${
            canProceed
              ? 'bg-green-600 text-white hover:bg-green-700 active:bg-green-800'
              : 'bg-gray-200 text-gray-500 cursor-not-allowed'
          }`}
          style={{ minHeight: '56px' }}
        >
          {isTransitioning ? (
            <>
              <Clock className="h-5 w-5 animate-spin" />
              <span>Departing...</span>
            </>
          ) : (
            <>
              <Play className="h-5 w-5" />
              <span>Let Go</span>
            </>
          )}
        </button>

        {/* Help Text */}
        {!canProceed && (
          <p className="text-center text-sm text-amber-600">
            Complete all required checks to depart
          </p>
        )}

        {/* Cancel Voyage */}
        <button
          onClick={handleCancelVoyage}
          className="w-full flex items-center justify-center gap-2 py-3 px-6 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
        >
          <X className="h-5 w-5" />
          <span>Cancel Voyage</span>
        </button>
      </div>

      {/* Fuel Input Modal for LET GO */}
      {showFuelModal && letGoConfirmed && (
        <FuelInputModal
          title="Departure Fuel Level"
          subtitle="Record fuel level before departing"
          onConfirm={handleFuelConfirm}
          onCancel={handleFuelCancel}
          required={true}
        />
      )}

      {/* FT-269: Event-time picker shown before any letGo() commit */}
      <TimePickerModal
        isOpen={showTimePicker}
        onClose={() => setShowTimePicker(false)}
        title="Confirm Let Go"
        vesselTimezone={currentVessel?.timezone || 'UTC'}
        onConfirm={handleTimePickerConfirm}
      />
    </div>
  );
};

export default PreDeparturePhase;
